import { useCallback, useEffect, useState } from "react";
import { App as VaultApp } from "./app/Shell";
import { Invite } from "./gate/Invite";
import { Onboard } from "./gate/Onboard";
import { ShareOpen } from "./gate/ShareOpen";
import { Unlock } from "./gate/Unlock";
import { api } from "./lib/api";
import { ToastProvider, VaultProvider, type SessionInfo } from "./state/store";
import { Toast } from "./ui/ds";
import { useIsMobile } from "./ui/hooks";

type Stage =
  | { kind: "boot" }
  | { kind: "setup" }
  | { kind: "unlock"; reason?: string }
  | { kind: "invite"; token: string }
  | { kind: "share"; id: string; fragment: string }
  | { kind: "app"; session: SessionInfo };

function initialStage(): Stage {
  const { pathname, hash } = window.location;
  const share = /^\/s\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (share) return { kind: "share", id: share[1]!, fragment: hash.slice(1) };
  if (pathname.startsWith("/invite")) {
    const token = hash.slice(1);
    window.history.replaceState(null, "", "/invite");
    return { kind: "invite", token };
  }
  return { kind: "boot" };
}

export function App() {
  const [stage, setStage] = useState<Stage>(initialStage);
  const mobile = useIsMobile();

  useEffect(() => {
    if (stage.kind !== "boot") return;
    api<{ needsSetup: boolean }>("/setup")
      .then((r) => setStage(r.needsSetup ? { kind: "setup" } : { kind: "unlock" }))
      .catch(() => setStage({ kind: "unlock" }));
  }, [stage.kind]);

  const onLock = useCallback((reason?: string) => {
    if (window.location.pathname !== "/") window.history.replaceState(null, "", "/");
    setStage({ kind: "unlock", reason });
  }, []);
  const enter = useCallback((session: SessionInfo) => setStage({ kind: "app", session }), []);

  return (
    <ToastProvider
      render={(t, close) =>
        t ? (
          <div className={`toast-wrap${mobile && stage.kind === "app" ? " is-mobile" : ""}`}>
            <Toast key={t.id} title={t.title} message={t.msg} tone={t.tone} onClose={close} />
          </div>
        ) : null
      }
    >
      {stage.kind === "boot" ? (
        <div className="gate">
          <div className="spinner" aria-label="Loading" />
        </div>
      ) : stage.kind === "setup" ? (
        <Onboard mode={{ kind: "setup" }} onDone={enter} />
      ) : stage.kind === "unlock" ? (
        <Unlock reason={stage.reason} onUnlocked={enter} />
      ) : stage.kind === "invite" ? (
        <Invite token={stage.token} onDone={enter} />
      ) : stage.kind === "share" ? (
        <ShareOpen id={stage.id} fragment={stage.fragment} />
      ) : (
        <VaultProvider session={stage.session} onLock={onLock}>
          <VaultApp />
        </VaultProvider>
      )}
    </ToastProvider>
  );
}
